import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { loginUser, loginWithGoogle as loginWithGoogleAPI } from '../api/auth'
import apiClient from '../api/client'
import { hasModuleAccess as staticHasModuleAccess, type ModuleName, type UserRole } from '../config/modulePermissions'
import { myRolePermissions } from '../api/rolePermissions'

interface User {
  username: string
  roles: string[]
  role: UserRole // Primary role (extracted from roles array)
}

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  loading: boolean
  isAdmin: boolean
  userRole: UserRole | undefined
  hasModuleAccess: (module: ModuleName) => boolean
  login: (username: string, password: string) => Promise<{ success: boolean; message: string }>
  loginWithGoogle: (credential: string) => Promise<{ success: boolean; message: string }>
  logout: () => void
  refreshPermissions: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [rolePermissions, setRolePermissions] = useState<null | { role: UserRole; modules: ModuleName[] }[]>(null)

  const refreshRolePermissions = async () => {
    try {
      const mine = await myRolePermissions()
      setRolePermissions([{ role: mine.role as UserRole, modules: (mine.modules || []) as ModuleName[] }])
    } catch {
      setRolePermissions(null)
    }
  }

  useEffect(() => {
    localStorage.removeItem('rolePermissions:v1')
    // Wstępny odczyt stanu (localStorage)
    const savedUser = localStorage.getItem('user')
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser)
        // Ensure role field exists (backwards compatibility)
        if (!userData.role && userData.roles && userData.roles.length > 0) {
          userData.role = userData.roles[0] as UserRole
        }
        // Ensure roles array exists (backwards compatibility)
        if ((!userData.roles || !Array.isArray(userData.roles) || userData.roles.length === 0) && userData.role) {
          userData.roles = [userData.role]
        }
        setUser(userData)
        setIsAuthenticated(true)
      } catch (error) {
        try { localStorage.removeItem('user') } catch {}
      }
    }

    // Weryfikacja sesji z backendem — jeśli brak ważnej sesji, wyczyść stan
    ;(async () => {
      try {
        await apiClient.get('/auth/user')
        await refreshRolePermissions()
      } catch {
        setUser(null)
        setIsAuthenticated(false)
        setRolePermissions(null)
        try {
          localStorage.removeItem('user')
          localStorage.removeItem('token')
        } catch {}
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const login = async (username: string, password: string) => {
    try {
      console.log('🔐 AuthContext: Starting login for', username)
      const response = await loginUser(username, password)
      console.log('📡 AuthContext: Login response:', response)

      if (response.status === 'success') {
        // Extract primary role (first role or 'user' as fallback)
        const primaryRole = (response.roles && response.roles.length > 0
          ? response.roles[0]
          : 'user') as UserRole

        const userData = {
          username: response.username,
          roles: response.roles,
          role: primaryRole
        }
        setUser(userData)
        setIsAuthenticated(true)
        localStorage.setItem('user', JSON.stringify(userData))

        // Save token if present
        if (response.token) {
          localStorage.setItem('token', response.token)
          console.log('🔑 AuthContext: Token saved to localStorage')
        }

        await refreshRolePermissions()

        console.log('✅ AuthContext: Login successful, user data saved:', userData)
        return { success: true, message: 'Zalogowano pomyślnie' }
      } else {
        console.log('❌ AuthContext: Login failed with status:', response.status)
        return { success: false, message: response.message || 'Błąd logowania' }
      }
    } catch (error) {
      console.error('❌ AuthContext: Login error:', error)
      return { success: false, message: 'Błąd połączenia z serwerem' }
    }
  }

  const loginWithGoogle = async (credential: string) => {
    try {
      console.log('🔑 AuthContext: Starting Google login')
      const response = await loginWithGoogleAPI(credential)
      console.log('📡 AuthContext: Google login response:', response)

      if (response.status === 'success') {
        const primaryRole = (response.roles && response.roles.length > 0
          ? response.roles[0]
          : 'user') as UserRole
        const userData = { username: response.username, roles: response.roles, role: primaryRole }
        setUser(userData)
        setIsAuthenticated(true)
        localStorage.setItem('user', JSON.stringify(userData))
        if (response.token) {
          localStorage.setItem('token', response.token)
          console.log('🔑 AuthContext: Google JWT saved to localStorage')
        }
        await refreshRolePermissions()
        console.log('✅ AuthContext: Google login successful:', userData)
        return { success: true, message: response.message }
      }
      return { success: false, message: response.message || 'Google login failed' }
    } catch (error) {
      console.error('❌ AuthContext: Google login error:', error)
      return { success: false, message: 'Błąd połączenia z serwerem' }
    }
  }

  const logout = () => {
    setUser(null)
    setIsAuthenticated(false)
    setRolePermissions(null)
    localStorage.removeItem('rolePermissions:v1')
    localStorage.removeItem('user')
    localStorage.removeItem('token')
  }

  const isAdmin = (user?.role === 'admin') || (user?.roles?.includes('admin') ?? false)
  const userRole = user?.role

  // Helper to check module access for current user
  const checkModuleAccess = (module: ModuleName): boolean => {
    // Prefer dynamic RBAC when available
    if (userRole && Array.isArray(rolePermissions)) {
      const rp = rolePermissions.find(r => r.role === userRole)
      if (rp) return rp.modules.includes(module)
    }
    // Fallback to static config
    return staticHasModuleAccess(userRole, module)
  }

  const value = {
    isAuthenticated,
    user,
    loading,
    isAdmin,
    userRole,
    hasModuleAccess: checkModuleAccess,
    login,
    loginWithGoogle,
    logout,
    refreshPermissions: refreshRolePermissions
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
